/// <amd-module name="CodeCoverage/CodeCoverageTab/Components/BuildCodeCoverage" />
import { CodeCoverageTabActionCreator } from "Actions/CodeCoverageTabActionCreator";
import { CodeCoverageTabActionHub, IConfigCodeCoverageDetails } from "Actions/CodeCoverageTabActionHub";
import { CodeCoverageDetailsList } from "CodeCoverage/CodeCoverageTab/Components/CodeCoverageDetailsList";
import { CodeCoverageSummaryView } from "CodeCoverage/CodeCoverageTab/Components/CodeCoverageSummaryView";
import { MessageArea } from "CodeCoverage/CodeCoverageTab/Components/MessageArea";
import * as Resources from "CodeCoverage/CodeCoverageTab/Resources";
import { PerfScenarios, TelemetryEvents } from "CodeCoverage/Common/Constants";
import { IRunViewArtifact, RunViewArtifact } from "CodeCoverage/Common/RunViewArtifact";
import { BuildConfigSelector } from "Components/BuildConfigSelector";
import { CodeCoverageHtmlSummary } from "Components/CodeCoverageHtmlSummary";
import { CodeCoverageModuleFilterBar } from "Components/CodeCoverageModuleFilterBar";
import * as React from "react";
import { BuildCodeCoverageStore, IBuildCodeCoverageState } from "Stores/BuildCodeCoverageStore";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IssuesCard } from "Build/Run/Components/IssuesCard";
import { MessageCard } from "VSSUI/Components/MessageCard/MessageCard";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { ZeroData } from "VSSUI/Components/ZeroData";
import { getAssetLocation } from "VSS/Platform/Location";
import { Toggle } from "VSSUI/Toggle";
import { ICodeCoverageHubSignalRSync, ICodeCoverageSignalRHubService } from "CodeCoverage/CodeCoverageSignalR/CodeCoverageHub";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ITfsPageService } from "Tfs/Platform/Page";
import { CodeCoverageFolderViewTree } from "./CodeCoverageFolderViewTree";


export interface IBuildCodeCoverageProps {
    runViewArtifact: IRunViewArtifact;
}

export class BuildCodeCoverage extends VssComponent<IBuildCodeCoverageProps, IBuildCodeCoverageState> {
    private _perfService: IVssPerformanceService;
    private _telemetryService: IVssTelemetryService;

    private _store: BuildCodeCoverageStore;
    private _actionCreator: CodeCoverageTabActionCreator;
    private _actionHub: CodeCoverageTabActionHub;
    private readonly BuildCodeCoverageChangeEvent = "buildCodeCoverageChangeEvent";
    private readonly NewUIForDotCoverageFeatureFlag = "TestManagement.Server.ModuleDetailsUIForDotCoverage";
    private folderViewCodeCoverageFF = isFeatureFlagEnabled(this.context.pageContext,"TestManagement.Server.FolderViewUICodeCoverage", false);
    private defaultCodeCoverageTab = isFeatureFlagEnabled(this.context.pageContext, "WebAccess.TestManagement.CodeCoverage.EnableTabByDefault", false);
    private _codeCoverageHub: ICodeCoverageSignalRHubService | undefined;
    private _codeCoverageSignalRHubNotificationSync: CodeCoverageSignalRHubNotificationsSync;

    constructor(props: IBuildCodeCoverageProps, context: IVssComponentContext) {
        super(props, context);
        this._perfService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    componentWillMount() {
        this._actionHub = CodeCoverageTabActionHub.getInstance();
        this._store = new BuildCodeCoverageStore(this.BuildCodeCoverageChangeEvent, this._actionHub);
        this._actionCreator = CodeCoverageTabActionCreator.getInstance(this.context);
      
        this.setState(this._store.getState());
        this._actionCreator.fetchBuildCodeCoverage(this.props.runViewArtifact);
        if (this.folderViewCodeCoverageFF) {
            this._actionCreator.fetchFolderViewBuildCodeCoverage(this.props.runViewArtifact);
        }
        this._store.addChangedListener(this._onStoreChanged);
        this._codeCoverageSignalRHubNotificationSync = new CodeCoverageSignalRHubNotificationsSync(this._actionCreator,this.props.runViewArtifact);
        this.watchForCodeCoverageUpdates();
    }

    componentDidMount() {
        this._perfService.endScenario(PerfScenarios.Area, PerfScenarios.CodeCoverageTab, true);
    }

    componentWillUnmount() {
        this.unwatchForCodeCoverageUpdates();
        this._store.removeChangedListener(this._onStoreChanged);
    }

    componentDidUpdate(){
        if(this.state.showCodeCoverageHTMLSummary || this.state.showCodeCoverageDetails || this.state.codeCoverageFailureMessage){
            this.unwatchForCodeCoverageUpdates();
        }
    }

  
    public render(): JSX.Element {
        if (this.defaultCodeCoverageTab) {
            if (!this.state || !(this.state.showCodeCoverageHTMLSummary || this.state.showCodeCoverageDetails
                || this.state.messageAreaText || this.state.codeCoverageFailureMessage || this.props.runViewArtifact.buildStatus!=2)) {
                return this._getSpinner();
            }
            return <div className="code-coverage-details">{this._getCodeCoverageUI()}</div>
        }
        if (!this.state || !(this.state.showCodeCoverageHTMLSummary || this.state.showCodeCoverageDetails || this.state.messageAreaText)) {
            return this._getSpinner();
        }
        return <div className="code-coverage-details">{this._getCodeCoverageUI()}</div>;
    }

    private _callFolderViewApi(toggleState: boolean) {
        this._actionHub.updateEnableFolderViewState.invoke(toggleState);
    }

    private _getCodeCoverageUI(): JSX.Element {
        if (this.state.showCodeCoverageHTMLSummary) {
            return (
                <CodeCoverageHtmlSummary
                    modernBrowserAvailable={this.state.modernBrowserAvailable}
                    codeCoverageSummaryLink={this.state.codeCoverageSummaryLink}
                />
            );
        }

        if (this.state.folderViewEnabled && this.folderViewCodeCoverageFF && this.state.folderViewBuildCodeCoverageDetails != null) {
            
            const folderViewBuildConfigList = this.state.folderViewBuildCodeCoverageDetails.buildConfigurations;
            const folderViewCoverage = this.state.folderViewBuildCodeCoverageDetails.folderViewCoverages[this.state.selectedFolderViewBuildConfiguration];
            this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.FolderViewCodeCoverageViewed, {});
            return (
                <div>
                        <div className="toggle-container">
                        <Toggle
                            ariaLabel="custom label"
                            text={"FolderView"}
                            checked={this.state.folderViewEnabled}
                            tooltipProps={{ text: "Use this to toggle between Folder view and Module view" }}
                            onChange={(event, value) => this._callFolderViewApi(value)}
                        />
                        <BuildConfigSelector configurations={folderViewBuildConfigList} />
                        <CodeCoverageSummaryView {...folderViewCoverage.summaryViewProps} />
                        <CodeCoverageFolderViewTree folderCoverageData={folderViewCoverage.folderCoverageData} />
                    </div>
                </div>

            );
        }

        if (this.state.showCodeCoverageDetails) {
            const buildConfigList = this.state.buildCodeCoverageDetails.buildConfigurations;
            const buildCoverage = this.state.buildCodeCoverageDetails.buildCoverages[this.state.selectedBuildConfiguration];
            const downloadLinkElement = this._getCodeCoverageDownloadLink(buildCoverage);
            if (this.folderViewCodeCoverageFF && this.state.folderViewBuildCodeCoverageDetails != null) {
                return (
                    <div>
                        <div>
                            <div className="toggle-container">
                                <Toggle
                                    ariaLabel="custom label"
                                    text={"FolderView"}
                                    checked={this.state.folderViewEnabled}
                                    tooltipProps={{ text: "Use this to toggle between Folder view and Module view" }}
                                    onChange={(event, value) => this._callFolderViewApi(value)}

                                />
                            </div>
                        </div>
                        <BuildConfigSelector configurations={buildConfigList} />
                        <CodeCoverageSummaryView {...buildCoverage.summaryViewProps} />
                        {downloadLinkElement}
                        <CodeCoverageModuleFilterBar />
                        <div className="toolbar-grid-separator" />
                        <CodeCoverageDetailsList
                            codeCoverageModuleList={buildCoverage.codeCoverageModuleList}
                            moduleFilterText={this.state.moduleFilterText}
                        />
                    </div>
                );
            } else {
                return (
                    <div>
                        <BuildConfigSelector configurations={buildConfigList} />
                        <CodeCoverageSummaryView {...buildCoverage.summaryViewProps} />
                        {downloadLinkElement}
                        <CodeCoverageModuleFilterBar />
                        <div className="toolbar-grid-separator" />
                        <CodeCoverageDetailsList
                            codeCoverageModuleList={buildCoverage.codeCoverageModuleList}
                            moduleFilterText={this.state.moduleFilterText}
                        />
                    </div>
                );
            }
        }

        if (this.defaultCodeCoverageTab) {
            
            if (this.state.codeCoverageFailureMessage == Resources.CodeCoverageNotConfigured ||
                this.state.codeCoverageFailureMessage == Resources.NoCodeCoverageFound) {
                this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.CodeCoverageFailureMessage, { state: this.state.codeCoverageFailureMessage });
                return (
                    <ZeroData imagePath={getAssetLocation(
                        this.context.pageContext,
                        "ms.vss-codecoverage-ci-web/view-common/Content/no-results-image.png"
                    )}
                        imageAltText=""
                        primaryText={Resources.CodeCoverageNotConfigured}
                        secondaryText={this.getCodeCoverageLearnLink()}
                    />
                );
            }

            if (this.state.codeCoverageFailureMessage) {
                this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.CodeCoverageFailureMessage, { state: this.state.codeCoverageFailureMessage });

                return (
                    <div className="flex-column">
                        <MessageCard severity={MessageCardSeverity.Error}>
                            {this.state.codeCoverageFailureMessage}
                        </MessageCard>
                        <br></br>
                        <IssuesCard>

                        </IssuesCard>
                    </div>
                );
            }

            if (this.props.runViewArtifact.buildStatus.valueOf() == 32 || this.props.runViewArtifact.buildStatus.valueOf() == 1) {
                let buildProgressMessage = "";
                switch(this.props.runViewArtifact.buildStatus.valueOf()){
                    case 32:
                        buildProgressMessage = Resources.BuildQueued
                        break;
                    case 1:
                        buildProgressMessage = Resources.BuildInProgress
                        break;
                }
                return (
                    <div className="flex-column">
                        <MessageCard>
                            {buildProgressMessage}
                        </MessageCard>
                    </div>
                );
            }
        }
        return (
            <div>
                <div className="code-coverage-download-link">
                    <span className="empty-results-message-bar">
                        <MessageArea text={this.state.messageAreaText} />
                    </span>
                </div>
            </div>
        ); 
    }

    private getCodeCoverageLearnLink: () => JSX.Element = () => {
        this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.NoCodeCoverageConfiguredClicked, { state: this.state.codeCoverageFailureMessage });
        return (
            <div>
              {Resources.CodeCoverageInformativeMessage}
                <Link className="margin-16"
                    href="https://learn.microsoft.com/en-us/azure/devops/pipelines/test/review-code-coverage-results?view=azure-devops"
                >
                    {Resources.LearnMoreText}
                </Link>
          </div>
        )
    }

    private _getSpinner: () => JSX.Element = () => <Spinner className="flex flex-grow absolute-fill" size={SpinnerSize.large} />;

    private _getCodeCoverageDownloadLink(buildCoverage: IConfigCodeCoverageDetails): JSX.Element | undefined {
        if (buildCoverage.coverageDownloadLink !== "") {
            return (
                <div className="code-coverage-download-link">
                    <Link
                        href={buildCoverage.coverageDownloadLink}
                        onClick={() => {
                            this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.DownloadLinkClicked, {});
                        }}
                    >
                        {Resources.DownloadCodeCoverageResults}
                    </Link>
                </div>
            );
        }
    }

    private _onStoreChanged = () => {
        this.setState(this._store.getState());
    };

    private async watchForCodeCoverageUpdates(): Promise<void> {
        if (this._codeCoverageHub) {
            return;
        }
        const isInProgressRefreshEnabled = isFeatureFlagEnabled( this.context.pageContext,"WebAccess.TestManagement.CodeCoverage.SignalRUpdatesForCodeCoverage",false);
        if (!isInProgressRefreshEnabled) {
            return;
        }
        // if(this.state.showCodeCoverageHTMLSummary || this.state.showCodeCoverageDetails || this.state.codeCoverageFailureMessage!=''){
        //     return;
        // }

        try {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            await contributionService.getContributionAsync("ms.vss-codecoverage-web.codecoverage-signalr");

            this._codeCoverageHub = this.context.pageContext.getService<ICodeCoverageSignalRHubService>("ICodeCoverageSignalRHub");

            if (this._codeCoverageHub) {
                const projectId = this._getProjectId();
                await this._codeCoverageHub.initialize(this.context.pageContext, projectId, this._codeCoverageSignalRHubNotificationSync);
                await this._codeCoverageHub.watchBuild(this.props.runViewArtifact.buildId);
                console.log("CodeCoverageHub: Successfully connected to Server")
            }
        } catch (error) {
            // Not throwing exceptions while handling signals.
            console.warn(`Error occurred while subscribing to codecoverage signals : ${error}`);           
        }
    }

    private unwatchForCodeCoverageUpdates(): void {
        if (!this._codeCoverageHub) {
            return;
        }
        this._codeCoverageHub .unwatchBuild(this.props.runViewArtifact.buildId)
                .then(() => {
                    if (this._codeCoverageHub) {
                        this._codeCoverageHub.dispose();
                        this._codeCoverageHub = undefined;
                    }
                    console.log("codeCoverageHub: Disconnected from server");
                })
                .catch((error: any) => {
                    console.error(`Error occurred while un-watching build : ${error}`);
                }); 
    }

    private _getProjectId(): string {
        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        if (tfsPageService) {
            const tfsPageData = tfsPageService.getData();
            if (tfsPageData && tfsPageData.project) {
                return tfsPageData.project.id;
            }
        }
        return "";
    }
}

Components.add("CC.BuildCodeCoverage", BuildCodeCoverage);

class CodeCoverageSignalRHubNotificationsSync implements ICodeCoverageHubSignalRSync{
    constructor(
        private _actionCreator: CodeCoverageTabActionCreator,
        _runViewArtifact : RunViewArtifact
    ){
        this._runViewArtifact = _runViewArtifact;
    }
   
    public _runViewArtifact:RunViewArtifact;
    
    public codeCoverageStatsChangedForBuild(buildId: number) : void{
        this._actionCreator.fetchBuildCodeCoverage(this._runViewArtifact);
        console.log("code coverage hub signal received from server")
    }
    
}
